import { useEffect, useState } from "react";
import { FileDown, Download, Receipt, Users } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

export default function ExpensesAdminPage() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (m = month) => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get(`/expenses/summary?month=${m}`),
        api.get(`/expenses?month=${m}`),
      ]);
      setSummary(s.data);
      setDetails(d.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem("uee_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/expenses/export?month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `expense_summary_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const grandTotal = summary?.rows?.reduce((acc, r) => acc + (r.total_amount || 0), 0) || 0;

  return (
    <div data-testid="admin-expenses-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
        <div>
          <div className="overline mb-1">Staff Expenses</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
            <Receipt className="w-6 h-6 text-amber-500" /> Monthly Expense Summary
          </h1>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Month</label>
            <Input
              type="month"
              value={month}
              onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
              className="rounded-none h-10 w-40"
              data-testid="exp-month-input"
            />
          </div>
          <Button onClick={downloadExcel} className="rounded-none btn-amber h-10 font-semibold" data-testid="exp-export-excel">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Total Staff</div>
          <div className="font-heading font-black text-3xl text-carbon">{summary?.rows?.length || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Total Entries</div>
          <div className="font-heading font-black text-3xl text-carbon">{details.length}</div>
        </div>
        <div className="bg-amber-500 p-5 text-slate-950">
          <div className="text-xs uppercase tracking-widest font-bold mb-2">Grand Total ({month})</div>
          <div className="font-heading font-black text-3xl">₹ {grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Per-staff summary */}
      <div className="mb-8">
        <div className="overline mb-3">Per-Staff Summary</div>
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3 font-semibold">Staff Name</th>
                <th className="text-left p-3 font-semibold">Email</th>
                <th className="text-right p-3 font-semibold">Sites Visited</th>
                <th className="text-right p-3 font-semibold">Travel Days</th>
                <th className="text-right p-3 font-semibold">Entries</th>
                <th className="text-right p-3 font-semibold">Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.rows || []).map((r) => (
                <tr key={r.staff_id} className="border-t border-slate-200">
                  <td className="p-3 font-semibold">{r.staff_name}</td>
                  <td className="p-3 font-mono text-xs">{r.staff_email}</td>
                  <td className="p-3 text-right">{r.total_sites}</td>
                  <td className="p-3 text-right">{r.total_days}</td>
                  <td className="p-3 text-right">{r.entries}</td>
                  <td className="p-3 text-right font-bold text-amber-700">₹ {r.total_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!summary || summary.rows?.length === 0) && (
            <div className="p-12 text-center text-slate-500 text-sm">No expenses for {month}.</div>
          )}
        </div>
      </div>

      {/* Detail list */}
      <div>
        <div className="overline mb-3">All Entries ({month})</div>
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3 font-semibold">Expense No</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-left p-3 font-semibold">Staff</th>
                <th className="text-left p-3 font-semibold">From → To</th>
                <th className="text-left p-3 font-semibold">Site</th>
                <th className="text-right p-3 font-semibold">Total</th>
                <th className="text-right p-3 font-semibold">PDF</th>
              </tr>
            </thead>
            <tbody>
              {details.map((e) => (
                <tr key={e.id} className="border-t border-slate-200">
                  <td className="p-3 font-mono text-xs">{e.expense_no}</td>
                  <td className="p-3 font-mono text-xs">{e.date}</td>
                  <td className="p-3">{e.submitted_by}</td>
                  <td className="p-3 text-xs">{e.travel_from} → {e.travel_to}</td>
                  <td className="p-3 text-xs">{e.site_name}</td>
                  <td className="p-3 text-right font-semibold">₹ {e.total_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right">
                    {e.pdf_url && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${e.pdf_url}`} target="_blank" rel="noopener noreferrer"
                         className="inline-flex p-2 hover:bg-slate-100">
                        <FileDown className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {details.length === 0 && (
            <div className="p-12 text-center text-slate-500 text-sm">No entries.</div>
          )}
        </div>
      </div>
    </div>
  );
}
